import React, { useRef, useState } from "react";
import { Box } from "@mui/material";
import InstallMobileRoundedIcon from "@mui/icons-material/InstallMobileRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import useInstallPrompt from "../../hooks/useInstallPrompt";
import { installGuideFor, isIos } from "../../utils/pwa";
import { BUTTON_RESET, C, FOCUS_RING, FONT_BODY, VISUALLY_HIDDEN } from "../../utils/designTokens";
import { IconTile, RowText, SettingsCard } from "../Settings/settingsUi";
import { STORES } from "../Orders/OrderDetail/AppPromo";

const GUIDES = [
  {
    key: "chrome",
    label: "Chrome / Edge",
    steps: [
      <>Open the <strong>⋮</strong> menu at the top right of the browser.</>,
      <>Choose <strong>Cast, save and share</strong>, then <strong>Install page as app</strong>.</>,
      <>Click <strong>Install</strong>.</>,
    ],
  },
  {
    key: "ios",
    label: "Safari on iPhone",
    steps: [
      <>Tap the <strong>Share</strong> button at the bottom of Safari.</>,
      <>Scroll down and choose <strong>Add to Home Screen</strong>.</>,
      <>Tap <strong>Add</strong>.</>,
    ],
  },
  {
    key: "mac",
    label: "Safari on Mac",
    steps: [
      <>Open the <strong>File</strong> menu in the menu bar.</>,
      <>Choose <strong>Add to Dock</strong>.</>,
      <>Click <strong>Add</strong>.</>,
    ],
  },
];

/**
 * Install the web app: the browser's own prompt when it offers one
 * (useInstallPrompt), and step-by-step instructions for every browser for
 * when it doesn't. The permanent way back to installing: the floating banner
 * (InstallPrompt) dismisses itself for good once closed.
 */
function InstallCard() {
  const { standalone, canPromptNatively, install } = useInstallPrompt();
  const [tab, setTab] = useState(() => installGuideFor());
  const tabRefs = useRef({});
  const guide = GUIDES.find((g) => g.key === tab) || GUIDES[0];

  // Left/right arrows move between the tabs, as a tab list should.
  const onTabKey = (e) => {
    const i = GUIDES.findIndex((g) => g.key === tab);
    const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = GUIDES[(i + step + GUIDES.length) % GUIDES.length].key;
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <>
      <SettingsCard>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: { xs: "20px", sm: "22px" }, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
          <IconTile>{standalone ? <CheckCircleRoundedIcon /> : <InstallMobileRoundedIcon />}</IconTile>
          <RowText
            title="Install the web app"
            sub={standalone ? "Already installed on this device" : "Add RushMyFood to your home screen or dock"}
          />
          {/* iPhone and iPad never offer the browser prompt: they follow the steps below. */}
          {!standalone && !isIos() && canPromptNatively && (
            <Box
              component="button"
              type="button"
              onClick={install}
              sx={{
                ...BUTTON_RESET,
                ...FOCUS_RING,
                flexShrink: 0,
                height: 44,
                px: 2.5,
                borderRadius: "12px",
                bgcolor: C.red,
                color: "#FFFFFF",
                fontFamily: FONT_BODY,
                fontSize: 15,
                fontWeight: 700,
                transition: "background-color .15s ease",
                "&:hover": { bgcolor: C.redHover },
              }}
            >
              Install
            </Box>
          )}
        </Box>

        {!standalone && (
          <Box sx={{ px: { xs: "20px", sm: "22px" }, py: 2.25, borderTop: `1px solid ${C.border}`, bgcolor: "#FBFAF8", fontFamily: FONT_BODY }}>
            <Box id="install-guide-title" sx={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
              No install button? Add it from your browser:
            </Box>
            <Box role="tablist" aria-labelledby="install-guide-title" sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
              {GUIDES.map((g) => {
                const selected = g.key === tab;
                return (
                  <Box
                    key={g.key}
                    component="button"
                    type="button"
                    role="tab"
                    id={`install-tab-${g.key}`}
                    aria-selected={selected}
                    aria-controls="install-steps"
                    tabIndex={selected ? 0 : -1}
                    ref={(el) => (tabRefs.current[g.key] = el)}
                    onClick={() => setTab(g.key)}
                    onKeyDown={onTabKey}
                    sx={{
                      ...BUTTON_RESET,
                      ...FOCUS_RING,
                      height: 36,
                      px: 1.75,
                      borderRadius: "999px",
                      border: `1px solid ${selected ? C.ink : C.borderStrong}`,
                      bgcolor: selected ? C.ink : C.bg,
                      color: selected ? "#FFFFFF" : C.ink,
                      fontSize: 14,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      transition: "background-color .15s ease, color .15s ease",
                      "&:hover": { borderColor: C.ink },
                    }}
                  >
                    {g.label}
                  </Box>
                );
              })}
            </Box>
            <Box
              component="ol"
              id="install-steps"
              role="tabpanel"
              aria-labelledby={`install-tab-${guide.key}`}
              sx={{ m: 0, mt: 2, p: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 1.25 }}
            >
              {guide.steps.map((s, i) => (
                <Box component="li" key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, fontSize: 14, color: C.ink, lineHeight: 1.5 }}>
                  <Box component="span" aria-hidden="true" sx={{ width: 26, height: 26, flexShrink: 0, borderRadius: "50%", bgcolor: C.bg, border: `1px solid ${C.borderStrong}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
                    {i + 1}
                  </Box>
                  <Box component="span" sx={{ pt: "2px", "& strong": { fontWeight: 700 } }}>{s}</Box>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </SettingsCard>

      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "flex-start", sm: "center" }, gap: 2, p: { xs: "20px", sm: "20px 22px" }, borderRadius: "20px", bgcolor: C.ink, color: "#FFFFFF", fontFamily: FONT_BODY }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: 16, fontWeight: 700 }}>Prefer the mobile app?</Box>
          <Box sx={{ fontSize: 14, color: "rgba(255,255,255,0.75)", mt: 0.25 }}>Live order tracking and faster reorders on your phone.</Box>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {STORES.map(({ href, small, big, Icon }) => (
            <Box
              key={big}
              component="a"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, height: 44, px: 1.5, borderRadius: "10px", bgcolor: "#FFFFFF", color: C.ink, textDecoration: "none", "&:hover": { bgcolor: C.surface }, "&:focus-visible": { outline: "3px solid #FFFFFF", outlineOffset: "3px" } }}
            >
              <Icon aria-hidden="true" sx={{ fontSize: 24 }} />
              <Box component="span" sx={{ display: "flex", flexDirection: "column", lineHeight: 1.05 }}>
                <Box component="span" sx={{ fontSize: 10, fontWeight: 500 }}>{small}</Box>
                <Box component="span" sx={{ fontSize: 15, fontWeight: 700 }}>{big}</Box>
              </Box>
              <Box component="span" sx={VISUALLY_HIDDEN}> (opens in a new tab)</Box>
            </Box>
          ))}
        </Box>
      </Box>
    </>
  );
}

export default React.memo(InstallCard);
